import React, { Suspense, lazy, useEffect } from "react";
import { ThemeProvider } from "@mui/material";
import { Route, Routes, useLocation } from "react-router-dom";

import { ModalWidgets } from "src/app/ui/ModalWidgets";
import { BackdropLoading } from "src/app/ui/BackdropLoading";
import { Dashboard } from "src/app/ui/Dashboard";
import { i18n, useLocale, useHreflangTags, useSeoTags } from "src/shared/i18n";

import { theme } from "./model/theme";

// Route-level code splitting: the Tools page pulls in @mux/mux-player-react and
// the WriteBetter widgets, and the Privacy Policy page pulls in its own large
// legal-copy bundle. Loading them lazily keeps those chunks off the main
// (Dashboard) bundle so they are only fetched when their route is visited.
const ToolsPage = lazy(() =>
  import("src/pages/ToolsPage/ToolsPage").then((m) => ({
    default: m.ToolsPage,
  })),
);
const PrivacyPolicy = lazy(() =>
  import("src/pages/PrivacyPolicy/PrivacyPolicy").then((m) => ({
    default: m.PrivacyPolicy,
  })),
);
const WriteBetterPrivacy = lazy(() =>
  import("src/pages/WriteBetterPrivacy/WriteBetterPrivacy").then((m) => ({
    default: m.WriteBetterPrivacy,
  })),
);

// The same page tree is mounted three times: once unprefixed (canonical
// English, so existing URLs are untouched), once under /en, once under /de.
const pageRoutes = (
  <>
    <Route index element={<Dashboard />} />
    <Route path="about-us/" element={<Dashboard />} />
    <Route path="services/" element={<Dashboard />} />
    <Route path="projects/" element={<Dashboard />} />
    <Route path="tools/" element={<ToolsPage />} />
    <Route path="privacy-policy/" element={<PrivacyPolicy />} />
    <Route path="writebetter/privacy/" element={<WriteBetterPrivacy />} />
    <Route path="blogs/" element={<Dashboard />} />
    <Route path="contacts/" element={<Dashboard />} />
  </>
);

const LocalizedApp: React.FC = () => {
  const { pathname } = useLocation();
  const { language } = useLocale();

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    document.documentElement.lang = language;
  }, [language]);

  useHreflangTags();
  useSeoTags();

  return (
    <ThemeProvider theme={theme(pathname)}>
      <Suspense fallback={null}>
        <Routes>{pageRoutes}</Routes>
      </Suspense>

      <ModalWidgets />
      <BackdropLoading />
    </ThemeProvider>
  );
};

export const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/de/*" element={<LocalizedApp />} />
      <Route path="/en/*" element={<LocalizedApp />} />
      <Route path="/*" element={<LocalizedApp />} />
    </Routes>
  );
};
